import { VISUAL_CONFIG } from "@shared/config";

// Track connection latency
export const latencyStats = {
  lastPing: 0,
  samples: [] as number[],
  average: 0,
  min: 0,
  max: 0,

  update(latency: number): void {
    // Ignore extreme outliers
    if (this.samples.length > 5 && latency > this.average * 10) {
      return;
    }

    this.samples.push(latency);

    // Keep only last 30 samples for accurate measurement
    if (this.samples.length > 30) {
      this.samples.shift();
    }

    // Calculate stats from valid samples (remove outliers)
    const validSamples = [...this.samples].sort((a, b) => a - b);

    // Remove highest 10% of values to eliminate outliers
    const trimCount = Math.floor(validSamples.length * 0.1);
    const trimmedSamples = validSamples.slice(
      0,
      validSamples.length - trimCount
    );

    this.min = Math.min(...trimmedSamples);
    this.max = Math.max(...trimmedSamples);

    // Calculate average
    const sum = trimmedSamples.reduce((a, b) => a + b, 0);
    this.average = sum / trimmedSamples.length;
  },

  // Format for display
  getDisplayText(): string {
    return `Latency: ${Math.round(this.average)}ms`;
  },
};

// Track when latency display was last updated to avoid frequent updates
let lastLatencyUpdate = 0;
const LATENCY_UPDATE_INTERVAL = 500; // Only update every 500ms

// Create latency display element
export function createLatencyDisplay(): HTMLElement {
  const latencyElement = document.createElement("div");
  latencyElement.id = "latency-display";
  latencyElement.style.position = "absolute";
  latencyElement.style.bottom = "25px";
  latencyElement.style.left = "10px";
  latencyElement.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
  latencyElement.style.color = "#0f0";
  latencyElement.style.fontFamily = "monospace";
  latencyElement.style.fontSize = "12px";
  latencyElement.style.padding = "5px";
  latencyElement.style.zIndex = "1000";
  latencyElement.textContent = latencyStats.getDisplayText();
  document.body.appendChild(latencyElement);
  return latencyElement;
}

// Update latency display
export function updateLatencyDisplay(): void {
  const now = Date.now();
  // Only update display periodically to avoid DOM thrashing
  if (now - lastLatencyUpdate < LATENCY_UPDATE_INTERVAL) {
    return;
  }

  lastLatencyUpdate = now;
  const latencyEl = document.getElementById("latency-display");
  if (!latencyEl) return;

  // Only show in ms if over 1ms
  const formattedAvg =
    latencyStats.average >= 1
      ? `${Math.round(latencyStats.average)}ms`
      : "<1ms";

  // Color coding based on latency
  let colorClass = "good";

  if (latencyStats.average > 100) {
    colorClass = "poor";
  } else if (latencyStats.average > 40) {
    colorClass = "fair";
  }

  latencyEl.textContent = formattedAvg;
  latencyEl.className = colorClass;

  // Only show warning for consistently high latency
  if (latencyStats.average > 150 && latencyStats.samples.length >= 10) {
    showNetworkWarning();
  }
}

// Combine network warning with sync warning to reduce UI clutter
let networkWarningTimeout: number | null = null;

export function showNetworkWarning(): void {
  if (networkWarningTimeout) {
    window.clearTimeout(networkWarningTimeout);
  }

  const warningEl = document.getElementById("network-warning");
  if (!warningEl) return;

  warningEl.style.opacity = "1";
  warningEl.textContent = "High latency detected";

  networkWarningTimeout = window.setTimeout(() => {
    if (warningEl) {
      warningEl.style.opacity = "0";
    }
    networkWarningTimeout = null;
  }, 3000);
}

export function showNotification(
  message: string,
  type: "info" | "warning" | "global" = "info"
): void {
  const notification = document.createElement("div");
  notification.style.position = "absolute";

  if (type === "global") {
    // Keep global notifications centered at top
    notification.style.top = "20px";
    notification.style.left = "50%";
    notification.style.transform = "translateX(-50%)";
  } else if (type === "warning") {
    // Keep warning notifications at bottom center
    notification.style.bottom = "20px";
    notification.style.left = "50%";
    notification.style.transform = "translateX(-50%)";
  } else {
    // Move info notifications to top-right below timer
    notification.style.top = "40px"; // Position just below the timer
    notification.style.right = "20px";
    notification.style.transform = "none";

    // Stack notifications if there are already some present
    const existingNotifications =
      document.querySelectorAll(".info-notification");
    if (existingNotifications.length > 0) {
      const offset = existingNotifications.length * 40; // 40px spacing between notifications
      notification.style.top = `${40 + offset}px`;
    }

    // Add class for easier selection
    notification.classList.add("info-notification");
  }

  if (type === "warning") {
    notification.style.backgroundColor = "rgba(255, 165, 0, 0.8)";
    notification.style.color = "#000";
    notification.style.border = "2px solid #ff8c00";
  } else if (type === "global") {
    notification.style.backgroundColor = "rgba(0,0,0,0.7)";
    notification.style.color = "#ffff00";
  } else {
    notification.style.backgroundColor = "rgba(0,0,0,0.8)";
    notification.style.color = "#fff"; // Better contrast with white text
    notification.style.border = "1px solid #444";
  }

  notification.style.padding = type === "info" ? "8px 12px" : "15px 20px"; // Smaller padding for info notifications
  notification.style.borderRadius = "6px";
  notification.style.fontFamily = "Arial, sans-serif";
  notification.style.fontSize =
    type === "info" ? "14px" : type === "global" ? "16px" : "18px"; // Smaller font for info
  notification.style.fontWeight = "bold";
  notification.style.textAlign = "center";
  notification.style.zIndex = "1000";
  notification.style.maxWidth = type === "info" ? "250px" : "400px"; // Limit width for info notifications
  notification.textContent = message;
  document.body.appendChild(notification);

  // Store the notification in a variable to reference for cleanup
  const notificationElement = notification;

  setTimeout(
    () => {
      // Remove the notification
      document.body.removeChild(notificationElement);

      // Reposition remaining notifications if this was an info type
      if (type === "info") {
        const remainingNotifications =
          document.querySelectorAll(".info-notification");
        remainingNotifications.forEach((elem, index) => {
          (elem as HTMLElement).style.top = `${40 + index * 40}px`;
        });
      }
    },
    type === "warning"
      ? VISUAL_CONFIG.UI.NOTIFICATION_DURATION.WARNING
      : type === "global"
      ? VISUAL_CONFIG.UI.NOTIFICATION_DURATION.GLOBAL
      : VISUAL_CONFIG.UI.NOTIFICATION_DURATION.INFO
  );
}

// Add reconnecting overlay
export function showReconnectingOverlay(
  attempt: number,
  maxAttempts: number,
  maxAttemptsReached = false,
  retryCallback?: () => void
): void {
  // Remove existing overlay if present
  hideReconnectingOverlay();

  const overlay = document.createElement("div");
  overlay.id = "reconnecting-overlay";
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
  overlay.style.display = "flex";
  overlay.style.flexDirection = "column";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.zIndex = "2000";
  overlay.style.color = "#fff";
  overlay.style.fontFamily = "Arial, sans-serif";

  const spinner = document.createElement("div");
  spinner.style.width = "60px";
  spinner.style.height = "60px";
  spinner.style.borderRadius = "50%";
  spinner.style.border = "6px solid transparent";
  spinner.style.borderTopColor = "#4a90e2";
  spinner.style.borderBottomColor = "#4a90e2";
  spinner.style.animation = "spin 1.5s linear infinite";

  // Add spinner animation
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0% { opacity: 0.4; }
      50% { opacity: 1; }
      100% { opacity: 0.4; }
    }
  `;
  document.head.appendChild(styleSheet);

  const statusText = document.createElement("h2");
  statusText.textContent = maxAttemptsReached
    ? "Connection Lost"
    : "Reconnecting to PacMassive...";
  statusText.style.margin = "20px 0";
  statusText.style.animation = "pulse 2s infinite";

  const attemptText = document.createElement("p");
  attemptText.textContent = maxAttemptsReached
    ? "Unable to reconnect after several attempts"
    : `Attempt ${attempt + 1} of ${maxAttempts}`;
  attemptText.style.marginBottom = "15px";

  overlay.appendChild(spinner);
  overlay.appendChild(statusText);
  overlay.appendChild(attemptText);

  // Add a retry button if max attempts reached
  if (maxAttemptsReached && retryCallback) {
    const retryButton = document.createElement("button");
    retryButton.textContent = "Try Again";
    retryButton.style.padding = "10px 20px";
    retryButton.style.backgroundColor = "#4a90e2";
    retryButton.style.color = "white";
    retryButton.style.border = "none";
    retryButton.style.borderRadius = "5px";
    retryButton.style.fontSize = "16px";
    retryButton.style.cursor = "pointer";
    retryButton.style.marginTop = "20px";

    // Add hover effect
    retryButton.onmouseover = () => {
      retryButton.style.backgroundColor = "#3a80d2";
    };
    retryButton.onmouseout = () => {
      retryButton.style.backgroundColor = "#4a90e2";
    };

    retryButton.onclick = retryCallback;

    // Add a refresh button as well
    const refreshButton = document.createElement("button");
    refreshButton.textContent = "Refresh Page";
    refreshButton.style.padding = "10px 20px";
    refreshButton.style.backgroundColor = "#555";
    refreshButton.style.color = "white";
    refreshButton.style.border = "none";
    refreshButton.style.borderRadius = "5px";
    refreshButton.style.fontSize = "16px";
    refreshButton.style.cursor = "pointer";
    refreshButton.style.marginTop = "10px";

    // Add hover effect
    refreshButton.onmouseover = () => {
      refreshButton.style.backgroundColor = "#444";
    };
    refreshButton.onmouseout = () => {
      refreshButton.style.backgroundColor = "#555";
    };

    refreshButton.onclick = () => {
      window.location.reload();
    };

    overlay.appendChild(retryButton);
    overlay.appendChild(refreshButton);
  } else {
    // Show a connection status text with tips
    const tipText = document.createElement("p");
    tipText.style.maxWidth = "400px";
    tipText.style.textAlign = "center";
    tipText.style.marginTop = "15px";
    tipText.style.fontSize = "14px";
    tipText.style.color = "#aaa";
    tipText.innerHTML =
      "Attempting to restore your connection...<br>If issues persist, check your internet connection.";

    overlay.appendChild(tipText);
  }

  document.body.appendChild(overlay);
}

export function hideReconnectingOverlay(): void {
  const existingOverlay = document.getElementById("reconnecting-overlay");
  if (existingOverlay) {
    document.body.removeChild(existingOverlay);
  }
}

export function createUIElements(): {
  scoreElement: HTMLElement;
  timerElement: HTMLElement;
  teamsScoreElement: HTMLElement;
} {
  const scoreElement = document.createElement("div");
  scoreElement.style.position = "absolute";
  scoreElement.style.top = "10px";
  scoreElement.style.left = "10px";
  scoreElement.style.color = "white";
  scoreElement.style.fontFamily = "Arial, sans-serif";
  scoreElement.style.fontSize = "16px";
  scoreElement.style.fontWeight = "bold";
  document.body.appendChild(scoreElement);

  const timerElement = document.createElement("div");
  timerElement.style.position = "absolute";
  timerElement.style.top = "10px";
  timerElement.style.right = "10px";
  timerElement.style.color = "white";
  timerElement.style.fontFamily = "Arial, sans-serif";
  timerElement.style.fontSize = "16px";
  timerElement.style.fontWeight = "bold";
  document.body.appendChild(timerElement);

  // Create teams score element in center top
  const teamsScoreElement = document.createElement("div");
  teamsScoreElement.style.position = "absolute";
  teamsScoreElement.style.top = "10px";
  teamsScoreElement.style.left = "50%";
  teamsScoreElement.style.transform = "translateX(-50%)";
  teamsScoreElement.style.color = "white";
  teamsScoreElement.style.fontFamily = "Arial, sans-serif";
  teamsScoreElement.style.fontSize = "20px";
  teamsScoreElement.style.fontWeight = "bold";
  teamsScoreElement.style.textAlign = "center";
  teamsScoreElement.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
  teamsScoreElement.style.padding = "5px 15px";
  teamsScoreElement.style.borderRadius = "5px";
  document.body.appendChild(teamsScoreElement);

  return { scoreElement, timerElement, teamsScoreElement };
}

export function updateUI(
  scoreElement: HTMLElement,
  timerElement: HTMLElement,
  score: number,
  teamScore: number,
  roundTime: number,
  hasRocket = false,
  dotsRemaining = 0
): void {
  // Update score display with enhanced styling
  scoreElement.innerHTML = `
    <span style="font-weight: bold;">Score: ${score}</span> |
    <span style="color: ${
      hasRocket ? "#ffff00" : "#ffffff"
    }">Team: ${teamScore}</span> |
    <span style="color: #00ffcc">Dots: ${dotsRemaining}</span>
    ${
      hasRocket
        ? ' | <span style="color: #ff6600; animation: pulse 1.5s infinite">🚀 Rocket Ready</span>'
        : ""
    }
  `;

  // Calculate timer values
  const totalSeconds = Math.max(0, Math.floor(roundTime / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // Create formatted time string that won't flash
  const timeStr = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  // Update timer with visual enhancements
  if (roundTime < 30000) {
    // Less than 30 seconds
    timerElement.innerHTML = `<span style="color: #ff4444; font-weight: bold; animation: pulse 1s infinite">⏱️ ${timeStr}</span>`;
  } else if (roundTime < 60000) {
    // Less than 1 minute
    timerElement.innerHTML = `<span style="color: #ffaa44; font-weight: bold;">⏱️ ${timeStr}</span>`;
  } else {
    timerElement.innerHTML = `<span style="font-weight: bold;">⏱️ ${timeStr}</span>`;
  }

  // Add CSS animation for pulsing effect if not already added
  if (!document.getElementById("ui-animations")) {
    const styleElement = document.createElement("style");
    styleElement.id = "ui-animations";
    styleElement.textContent = `
      @keyframes pulse {
        0% { opacity: 0.8; }
        50% { opacity: 1; }
        100% { opacity: 0.8; }
      }
    `;
    document.head.appendChild(styleElement);
  }
}

// Update instructions to include rocket usage
export function updateInstructions(): void {
  const instructionsElement = document.getElementById("instructions");
  if (instructionsElement) {
    instructionsElement.innerHTML = `
      <h3>PacMassive Controls</h3>
      <p>Move with <span class="key">W</span><span class="key">A</span><span class="key">S</span><span class="key">D</span> or arrow keys</p>
      <p>Collect dots for points and avoid ghosts!</p>
      <p>Press <span class="key">SPACE</span> to use rocket when available</p>
      <p>Press <span class="key">H</span> to hide these instructions</p>
    `;
  }
}

// Sync warning improvements
let syncWarningCount = 0;
let syncWarningTimeout: number | null = null;

export function showSyncWarning(): void {
  syncWarningCount++;

  const syncWarningEl = document.getElementById("sync-warning");
  if (!syncWarningEl) return;

  // Clear existing timeout
  if (syncWarningTimeout) {
    window.clearTimeout(syncWarningTimeout);
  }

  // Show warning with count if multiple
  syncWarningEl.style.opacity = "1";
  syncWarningEl.textContent =
    syncWarningCount > 1
      ? `Synchronizing with server (${syncWarningCount})`
      : "Synchronizing with server";

  // Auto-hide after delay
  syncWarningTimeout = window.setTimeout(() => {
    if (syncWarningEl) {
      syncWarningEl.style.opacity = "0";
      syncWarningCount = 0;
    }
    syncWarningTimeout = null;
  }, 2000);
}

// New function to update the teams score display
export function updateTeamsScore(
  teamsScoreElement: HTMLElement,
  teamAScore: number,
  teamBScore: number
): void {
  // Update teams score with vibrant colors and visual separation
  teamsScoreElement.innerHTML = `
    <span style="color: #ff5555; font-weight: bold; text-shadow: 0 0 5px rgba(255, 0, 0, 0.7);">🔴 ${teamAScore}</span>
    <span style="margin: 0 10px; color: #ffffff;">VS</span>
    <span style="color: #5555ff; font-weight: bold; text-shadow: 0 0 5px rgba(0, 0, 255, 0.7);">🔵 ${teamBScore}</span>
  `;
}
